/**
 * Logger utility for production-safe logging
 * Prevents information leakage in production while maintaining debug capabilities
 */

const isProduction = process.env.NODE_ENV === 'production';

/**
 * Logger object with different log levels
 * In production, only errors are logged (and can be sent to error tracking service)
 * In development, all logs are shown in console
 */
export const logger = {
  /**
   * Log errors
   * In production: Only log to error tracking service (if configured)
   * In development: Log to console
   */
  error: (...args) => {
    if (!isProduction) {
      console.error('[ERROR]', ...args);
    } else {
      // In production, you can send to error tracking service here
      // Example: Sentry.captureException(args[0]);
      // For now, we'll silently log to avoid exposing errors to users
    }
  },

  /**
   * Log warnings
   * Only shown in development
   */
  warn: (...args) => {
    if (!isProduction) {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * Log informational messages
   * Only shown in development
   */
  info: (...args) => {
    if (!isProduction) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Log debug messages
   * Only shown in development
   */
  debug: (...args) => {
    if (!isProduction) {
      console.debug('[DEBUG]', ...args);
    }
  }
};

/**
 * Safe error handler that prevents information disclosure
 * @param {Error} error - The error object
 * @param {string} userMessage - User-friendly error message
 * @returns {string} - Safe error message for users
 */
export const handleError = (error, userMessage = 'An error occurred. Please try again or contact support.') => {
  // Log full error details for debugging (only in development)
  logger.error('Error details:', error);
  
  // In production, return generic user message
  // In development, return more details for debugging
  if (isProduction) {
    return userMessage;
  } else {
    // In development, show more details
    return error?.message || userMessage;
  }
};

export default logger;





